import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const spring = { type: "spring", stiffness: 380, damping: 26, mass: 0.7 };

const fill = {
  rest: { x: "-101%" },
  hover: { x: "0%", transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const arrowOut = {
  rest: { x: "0%", opacity: 1 },
  hover: { x: "160%", opacity: 0, transition: spring },
};

const arrowIn = {
  rest: { x: "-160%", opacity: 0 },
  hover: { x: "0%", opacity: 1, transition: spring },
};

function Arrow({ className = "h-3 w-3" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const sizes = {
  md: { pad: "px-6 py-3", text: "text-[12px]", gap: "gap-2.5", chip: "h-6 w-6", arrow: "h-3 w-3" },
  sm: { pad: "px-4 py-2", text: "text-[11px]", gap: "gap-2", chip: "h-5 w-5", arrow: "h-2.5 w-2.5" },
};

// "dark"  -> glass pill that suits dark navy backgrounds (default)
// "light" -> white pill with blue text, fills blue on hover
const variants = {
  dark: {
    base: "bg-white/[0.06] text-white ring-[#4268FF]/50 backdrop-blur-sm shadow-[0_4px_14px_-6px_rgba(17,59,225,0.6)]",
    baseActive: "ring-[#4268FF]",
    chip: "bg-[#4268FF] text-white",
    chipActive: "bg-white/25",
  },
  light: {
    base: "bg-white text-[#113BE1] ring-[#4268FF]/25 shadow-[0_4px_14px_-4px_rgba(17,59,225,0.35)]",
    baseActive: "text-white",
    chip: "bg-[#113BE1] text-white",
    chipActive: "bg-white/25",
  },
};

export default function CourseButton({
  label,
  variant = "dark",
  // "md" (default, used by the profile page's course cards) or "sm" (compact)
  size = "md",
  // hover fill
  buttonColor = "bg-gradient-to-r from-[#113BE1] to-[#4268FF]",
  // true when an ancestor (e.g. the card) is hovered, so the button shows its
  // hover state even when the cursor isn't directly over it
  forceHover = false,
}) {
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const v = variants[variant] ?? variants.dark;
  const s = sizes[size] ?? sizes.md;
  const isHovered = hovered || forceHover;
  const active = isHovered && !reduceMotion;

  return (
    <motion.span
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      initial="rest"
      animate={active ? "hover" : "rest"}
      whileTap={reduceMotion ? undefined : { scale: 0.96 }}
      transition={spring}
      className={`
        group/btn relative isolate flex w-full select-none items-center justify-center ${s.gap}
        overflow-hidden rounded-full ${s.pad}
        ${s.text} font-bold uppercase tracking-wide
        ring-1 ring-inset
        transition-[color,box-shadow,transform] duration-300 ease-in-out
        ${v.base}
        ${active ? `scale-[1.03] text-white shadow-[0_14px_32px_-8px_rgba(66,104,255,0.85)] ${v.baseActive}` : ""}
      `}
    >
      <motion.span
        aria-hidden
        variants={reduceMotion ? undefined : fill}
        className={`absolute inset-0 -z-10 rounded-full ${buttonColor} ${
          reduceMotion
            ? `transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`
            : ""
        }`}
      />

      <span className="relative">{label}</span>

      <span
        className={`relative flex ${s.chip} items-center justify-center overflow-hidden rounded-full transition-colors duration-300 ${v.chip} ${
          active ? v.chipActive : ""
        }`}
      >
        <motion.span
          variants={reduceMotion ? undefined : arrowOut}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Arrow className={s.arrow} />
        </motion.span>
        <motion.span
          variants={reduceMotion ? undefined : arrowIn}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Arrow className={s.arrow} />
        </motion.span>
      </span>
    </motion.span>
  );
}